import { safeSetItem } from "@/lib/storage/safe-local";
import { useEffect, useState } from "react";
import { track } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";

const KEY = "cb16.wishlist";
const EVT = "cb16:wishlist";

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function write(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    safeSetItem(KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* ignore */
  }
}

export function toggleWishlist(productId: string): boolean {
  const ids = read();
  const has = ids.includes(productId);
  const next = has ? ids.filter((i) => i !== productId) : [...ids, productId];
  write(next);
  track(has ? CIO_EVENTS.WISHLIST_REMOVED : CIO_EVENTS.WISHLIST_ADDED, {
    product_id: productId,
  });
  return !has;
}

export function useIsWishlisted(productId: string): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(read().includes(productId));
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [productId]);
  return on;
}

export function getWishlist(): string[] {
  return read();
}

export function removeFromWishlist(productId: string) {
  const next = read().filter((i) => i !== productId);
  write(next);
  track(CIO_EVENTS.WISHLIST_REMOVED, { product_id: productId });
}

export function clearWishlist() {
  write([]);
}

export function useWishlist(): string[] {
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    const sync = () => setIds(read());
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return ids;
}
